/**
 * Notification Preferences Hooks - Sprint 19b
 *
 * React Query hooks for notification preferences.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import * as notificationsService from '../services/notifications.service';

import type { UpdateNotificationPreferencesInput } from '@bstock/shared';

/**
 * Query key factory for notification preferences
 */
export const preferencesKeys = {
  all: ['notification-preferences'] as const,
};

/**
 * Hook to fetch user's notification preferences
 */
export function useNotificationPreferences() {
  return useQuery({
    queryKey: preferencesKeys.all,
    queryFn: notificationsService.getPreferences,
  });
}

/**
 * Hook to update notification preferences
 */
export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateNotificationPreferencesInput) =>
      notificationsService.updatePreferences(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: preferencesKeys.all });
      toast.success('Preferences mises a jour');
    },
    onError: () => {
      toast.error('Erreur lors de la mise a jour des preferences');
    },
  });
}

/**
 * Hook to reset notification preferences to defaults
 */
export function useResetNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationsService.resetPreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: preferencesKeys.all });
      toast.success('Preferences reinitialisees');
    },
    onError: () => {
      toast.error('Erreur lors de la reinitialisation des preferences');
    },
  });
}
